import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigContext } from './utils/Config';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  List,
  ListItem,
  ListIcon,
  Badge,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  useColorModeValue
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

const SubscriptionPurchase = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Mock subscription plans data
  const subscriptionPlans = [
    {
      id: 'monthly',
      name: t('subscriptionPurchase.plans.monthly.name'),
      price: '$9.99',
      period: t('subscriptionPurchase.plans.monthly.period'),
      description: t('subscriptionPurchase.plans.monthly.description'),
      popular: false,
      features: [
        t('subscriptionPurchase.features.unlimitedAccess'),
        t('subscriptionPurchase.features.monthlyIssues'),
        t('subscriptionPurchase.features.mobileAccess'),
        t('subscriptionPurchase.features.customerSupport')
      ]
    },
    {
      id: 'yearly',
      name: t('subscriptionPurchase.plans.yearly.name'),
      price: '$99.99',
      period: t('subscriptionPurchase.plans.yearly.period'),
      description: t('subscriptionPurchase.plans.yearly.description'),
      popular: true,
      originalPrice: '$119.88',
      savings: t('subscriptionPurchase.plans.yearly.savings'),
      features: [
        t('subscriptionPurchase.features.unlimitedAccess'),
        t('subscriptionPurchase.features.monthlyIssues'),
        t('subscriptionPurchase.features.mobileAccess'),
        t('subscriptionPurchase.features.customerSupport'),
        t('subscriptionPurchase.features.exclusiveContent'),
        t('subscriptionPurchase.features.archiveAccess')
      ]
    },
    {
      id: 'lifetime',
      name: t('subscriptionPurchase.plans.lifetime.name'),
      price: '$299.99',
      period: t('subscriptionPurchase.plans.lifetime.period'),
      description: t('subscriptionPurchase.plans.lifetime.description'),
      popular: false,
      features: [
        t('subscriptionPurchase.features.unlimitedAccess'),
        t('subscriptionPurchase.features.monthlyIssues'),
        t('subscriptionPurchase.features.mobileAccess'),
        t('subscriptionPurchase.features.customerSupport'),
        t('subscriptionPurchase.features.exclusiveContent'),
        t('subscriptionPurchase.features.archiveAccess'),
        t('subscriptionPurchase.features.earlyAccess'),
        t('subscriptionPurchase.features.prioritySupport')
      ]
    }
  ];

  const handlePurchase = async (plan) => {
    setIsLoading(true);
    setSelectedPlan(plan.id);

    try {
      // Simulate purchase process
      // In real implementation, this would integrate with your payment processor
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Redirect to payment processor or handle purchase
      // For now, we'll just show an alert and redirect
      alert(t('subscriptionPurchase.redirectingToPayment'));
      
      // Example: Redirect to payment URL with plan information
      const paymentUrl = `https://your-payment-processor.com/checkout?plan=${plan.id}&user=${config.userData?.uuid}`;
      window.location.href = paymentUrl;
      
    } catch (error) {
      console.error('Purchase error:', error);
      alert(t('subscriptionPurchase.purchaseError'));
    } finally {
      setIsLoading(false);
      setSelectedPlan(null);
    }
  };

  // Check if user is logged in
  const isLoggedIn = config.userData && config.userData.bo_access_token;

  if (!isLoggedIn) {
    return (
      <Box p={8} textAlign="center">
        <Alert status="warning" mb={6}>
          <AlertIcon />
          <AlertDescription>
            {t('subscriptionPurchase.loginRequired')}
          </AlertDescription>
        </Alert>
        <Button colorScheme="blue" onClick={() => window.location.href = '/login'}>
          {t('subscriptionPurchase.goToLogin')}
        </Button>
      </Box>
    );
  }

  return (
    <Box className="subscription-purchase" p={6} maxW="1200px" mx="auto">
      {/* Header */}
      <VStack spacing={4} textAlign="center" mb={8}>
        <Heading as="h1" size="xl">
          {t('subscriptionPurchase.title')}
        </Heading>
        <Text fontSize="lg" color="gray.600" maxW="600px">
          {t('subscriptionPurchase.subtitle')}
        </Text>
      </VStack>

      {/* Subscription Plans */}
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        gap={6}
        justify="center"
        align="stretch"
      >
        {subscriptionPlans.map((plan) => (
          <Card
            key={plan.id}
            bg={cardBg}
            borderWidth="2px"
            borderColor={plan.popular ? 'blue.500' : borderColor}
            position="relative"
            flex="1"
            maxW={{ base: '100%', lg: '350px' }}
            _hover={{
              transform: 'translateY(-4px)',
              shadow: 'xl'
            }}
            transition="all 0.2s"
          >
            {plan.popular && (
              <Badge
                colorScheme="blue"
                position="absolute"
                top="-12px"
                left="50%"
                transform="translateX(-50%)"
                px={4}
                py={1}
                borderRadius="full"
                fontSize="sm"
              >
                {t('subscriptionPurchase.mostPopular')}
              </Badge>
            )}

            <CardHeader textAlign="center" pb={4}>
              <Heading as="h3" size="lg" mb={2}>
                {plan.name}
              </Heading>
              <VStack spacing={1}>
                <HStack align="baseline" justify="center">
                  {plan.originalPrice && (
                    <Text
                      fontSize="lg"
                      color="gray.500"
                      textDecoration="line-through"
                    >
                      {plan.originalPrice}
                    </Text>
                  )}
                  <Text fontSize="3xl" fontWeight="bold" color="blue.600">
                    {plan.price}
                  </Text>
                </HStack>
                <Text color="gray.600">{plan.period}</Text>
                {plan.savings && (
                  <Badge colorScheme="green" mt={1}>
                    {plan.savings}
                  </Badge>
                )}
              </VStack>
            </CardHeader>

            <CardBody pt={0}>
              <Text fontSize="sm" color="gray.600" mb={6} textAlign="center">
                {plan.description}
              </Text>

              <List spacing={3} mb={8}>
                {plan.features.map((feature, index) => (
                  <ListItem key={index}>
                    <ListIcon as={CheckIcon} color="green.500" />
                    {feature}
                  </ListItem>
                ))}
              </List>

              <Button
                colorScheme={plan.popular ? 'blue' : 'gray'}
                size="lg"
                width="100%"
                isLoading={isLoading && selectedPlan === plan.id}
                loadingText={t('subscriptionPurchase.processing')}
                onClick={() => handlePurchase(plan)}
                _hover={{
                  transform: 'translateY(-1px)'
                }}
              >
                {t('subscriptionPurchase.choosePlan')}
              </Button>
            </CardBody>
          </Card>
        ))}
      </Flex>

      {/* Additional Information */}
      <VStack spacing={4} textAlign="center" mt={12}>
        <Text color="gray.600">
          {t('subscriptionPurchase.securePayment')}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {t('subscriptionPurchase.cancelAnytime')}
        </Text>        
      </VStack>
    </Box>
  );
};

export default SubscriptionPurchase;